import React, { useState } from "react";
import {
  Col,
  Container,
  Row,
  Image,
  Button,
  Modal,
  Form,
  Table,
} from "react-bootstrap";
import PageHeading from "components/shared/PageHeading";
import deleteIcon from "../../../assets/delete.svg";
import Flag from "react-world-flags"; // Import the react-world-flags package
import CapitalizeFirst from "components/shared/CapitalizeFirst";
import { toast } from "react-toastify";
import {
  deleteWordFromStudySet,
  reportStudySet,
} from "services/Teacher/Vocabulary.service"; // Assuming these APIs exist
import nodataimage from "../../../assets/nodataimage.svg";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faFlag } from "@fortawesome/free-solid-svg-icons";
import { reportExercise } from "services/Teacher/Exercise.service";
import { useAuth } from "utils/AuthContext";
import { useTranslation } from "react-i18next";

const VocabularyListReports = ({
  studySetDetail,
  sourceLanguageTitle,
  targetLanguageTitle,
  studySetId = null,
  editable,
  enabledDelete = true,
  libraryStatus = false,
  setWordDetails = "",
  handleShowModal,
  reload = () => {},
  onDeleteWord, // Custom delete handler for exercises
}) => {
  const { t } = useTranslation();
  const { userRoles } = useAuth();
  const [loadingWord, setLoadingWord] = useState(null); // Track loading state for each word
  const [showReportModal, setShowReportModal] = useState(false);
  const [selectedReason, setSelectedReason] = useState("");
  const [selectedWords, setSelectedWords] = useState([]);
  const [description, setDescription] = useState("");
  const [errors, setErrors] = useState({});
  const [loadingReport, setLoadingReport] = useState(false); // Loading state for report submission

  const getFlagCode = (languageTitle) => {
    switch (languageTitle) {
      case "English":
        return "GB"; // UK flag for English
      case "Swedish":
        return "SE"; // Swedish flag
      case "Italian":
        return "IT"; // Italian flag
      case "French":
        return "FR"; // French flag
      case "Spanish":
        return "ES"; // Spanish flag
      default:
        return "GB"; // Default to UK flag if the language is not recognized
    }
  };

  const handleDeleteWord = async (wordId, word) => {
    // If custom delete handler is provided, use it (for exercises)
    if (onDeleteWord) {
      onDeleteWord(word);
      return;
    }

    // Default behavior for study sets
    const toastId = toast.loading(t("Deleting word..."));

    try {
      setLoadingWord(wordId); // Set the word as being deleted

      // Delete the word from the study set
      await deleteWordFromStudySet(studySetId, wordId);

      // Show success toast
      toast.update(toastId, {
        render: t("Word deleted successfully!"),
        type: "success",
        isLoading: false,
        autoClose: 3000,
      });

      // Reload study set details after deletion
      reload();
    } catch (error) {
      console.error("Error deleting word:", error);

      // Show error toast
      toast.update(toastId, {
        render: t("Error deleting word."),
        type: "error",
        isLoading: false,
        autoClose: 5000,
      });
    } finally {
      setLoadingWord(null); // Reset the loading state after the operation
    }
  };

  // Handle showing the report modal
  const handleShowReportModal = () => setShowReportModal(true);
  const handleCloseReportModal = () => setShowReportModal(false);

  const handleReportSubmit = async () => {
    // Validate the report form
    let validationErrors = {};
    if (!selectedReason) {
      validationErrors.selectedReason = t("Reason for reporting is required.");
    }
    if (!description) {
      validationErrors.description = t("Description is required.");
    }
    if (selectedWords.length === 0) {
      validationErrors.selectedWords = t("At least one word must be selected.");
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoadingReport(true);
    const reportPayload = {
      content: studySetId,
      contentType: "StudySet",
      type: selectedReason,
      selectedWords,
      reason: description,
    };

    // Sample API Call to report the issue
    try {
      await reportExercise(reportPayload);
      toast.success(t("Report submitted successfully!"));
      setShowReportModal(false);
      // Reset form state after successful report submission
      setSelectedReason("");
      setSelectedWords([]);
      setDescription("");
    } catch (error) {
      toast.error(t("Error submitting report!"));
      console.error("Error reporting study set:", error);
    } finally {
      setLoadingReport(false);
    }
  };

  return (
    <>
      <Container fluid>
        {/* Report Button */}
        <div className="w-100 text-end">
          {/* {libraryStatus && (
            <Button
              className="me-0"
              variant="danger"
              onClick={handleShowReportModal}
            >
              <FontAwesomeIcon icon={faFlag} className="me-2" />
              Report
            </Button>
          )} */}
        </div>

        {/* Report Modal */}
        <Modal
          size="lg"
          show={showReportModal}
          onHide={handleCloseReportModal}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>{t("Report Study Set")}</Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ minHeight: "400px", overflow: "auto" }}>
            <Form>
              {/* Reason Dropdown */}
              <Form.Group controlId="reason">
                <Form.Label>{t("Reason for Report")}</Form.Label>
                <Form.Control
                  as="select"
                  value={selectedReason}
                  onChange={(e) => setSelectedReason(e.target.value)}
                  isInvalid={!!errors.selectedReason}
                >
                  <option value="">{t("Select a reason")}</option>
                  <option value="incorrect-translation">
                    {t("Incorrect Translation")}
                  </option>
                  <option value="wrong-meaning">{t("Wrong Meaning")}</option>
                  <option value="grammar-error">{t("Grammar Error")}</option>
                  <option value="irrelevant-word">
                    {t("Irrelevant Word")}
                  </option>
                  <option value="other">{t("Other")}</option>
                </Form.Control>
                {errors.selectedReason && (
                  <p style={{ color: "red" }}>{errors.selectedReason}</p>
                )}
              </Form.Group>

              {/* Words Selection Table */}
              <Form.Label className="mt-3 mb-2">
                {t("Select words to report")}
              </Form.Label>
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th>{t("Select")}</th>
                    <th>{t("Word")}</th>
                    <th>{t("Translation")}</th>
                  </tr>
                </thead>
                <tbody>
                  {studySetDetail?.map((word, index) => (
                    <tr key={index}>
                      <td>
                        <Form.Check
                          type="checkbox"
                          checked={selectedWords.includes(word._id)}
                          onChange={() => {
                            if (selectedWords.includes(word._id)) {
                              setSelectedWords(
                                selectedWords.filter((id) => id !== word._id)
                              );
                            } else {
                              setSelectedWords([...selectedWords, word._id]);
                            }
                          }}
                        />
                      </td>
                      <td>{word?.title}</td>
                      <td>
                        {word.translations?.map((t) => t.text).join(", ")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              {errors.selectedWords && (
                <p style={{ color: "red" }}>{errors.selectedWords}</p>
              )}

              {/* Description Textbox */}
              <Form.Group controlId="description">
                <Form.Label>{t("Explaination")}</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  isInvalid={!!errors.description}
                  maxLength={500}
                />
                {errors.description && (
                  <p style={{ color: "red" }}>{errors.description}</p>
                )}
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            {/* Submit Report Button */}
            <div className="d-flex justify-content-end">
              <Button
                variant="primary"
                className="button-style"
                onClick={handleReportSubmit}
                disabled={loadingReport}
              >
                {loadingReport ? t("Submitting...") : t("Submit Report")}
              </Button>
            </div>
          </Modal.Footer>
        </Modal>

        {studySetDetail?.length === 0 && (
          <div className="bg-white my-auto pt-3 rounded-3 text-center">
            <Image src={nodataimage} alt="No Data" className="img-fluid" />
            <p>{t("No words found")}</p>
          </div>
        )}

        {studySetDetail?.map((report, reportIndex) => (
          <div key={reportIndex} className="shadow my-4 p-2 rounded-3">
            <Row className="my-3">
              <Col xs={12}>
                <div className="px-3 border-0 report-listing">
                  <div className="d-flex justify-content-between">
                    <div className="d-flex align-items-center gap-2">
                      <h4 className="mb-0">
                        <CapitalizeFirst text={report?.title} />
                      </h4>
                      <div className="d-flex align-items-center">
                        <Flag
                          code={getFlagCode(
                            report?.sourceLanguage?.title || sourceLanguageTitle
                          )}
                          style={{ width: 24, height: 24, marginRight: 8 }}
                        />
                        <span>{report?.sourceLanguage?.title}</span>
                      </div>
                    </div>
                    {enabledDelete && userRoles !== "user" && (
                      <div className="text-center" role="button">
                        <span
                          className="px-2 py-2 text-danger"
                          onClick={() => handleDeleteWord(report._id, report)} // Delete button logic
                        >
                          <Image
                            src={deleteIcon}
                            style={{
                              cursor:
                                loadingWord === report._id
                                  ? "not-allowed"
                                  : "pointer", // Disable button during deletion
                              opacity: loadingWord === report._id ? 0.5 : 1, // Dim button while loading
                            }}
                          />
                        </span>

                        <span
                          className="px-2 py-2 text-danger"
                          onClick={() => {
                            setWordDetails(report);
                            handleShowModal();
                          }} // Delete button logic
                        >
                          <FontAwesomeIcon icon={faEdit} color="#303030" />
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Meaning */}
                  <ul className="mt-2 mb-0 ps-4 nested-report-listing">
                    {report?.meaning.map((item, index) => (
                      <li
                        key={`${report?.id}-${index}`}
                        style={{
                          color: "#303972",
                          fontSize: "16px",
                          fontWeight: 400,
                          lineHeight: "24px",
                        }}
                      >
                        {item}
                      </li>
                    ))}
                  </ul>

                  {/* Sentence */}
                  <div>
                    <p
                      className="mb-1"
                      style={{
                        color: "#303972",
                        fontSize: "18px",
                        fontWeight: 400,
                        lineHeight: "24px",
                      }}
                    >
                      <span
                        style={{
                          color: "#303972",
                          marginRight: "5px",
                          fontWeight: "bold",
                          fontSize: "18px",
                          lineHeight: "24px",
                        }}
                      >
                        {t("Sentence")}:
                      </span>
                      <CapitalizeFirst text={report?.sentence} />
                    </p>
                  </div>

                  {/* Translations with Target Language Flag */}
                  <b
                    style={{
                      color: "#303972",
                      fontSize: "18px",
                      lineHeight: "24px",
                    }}
                  >
                    {t("Translation")}:
                  </b>
                  {report?.translations?.map((translation, index) => (
                    <div
                      key={`${report?.id}-${index}`}
                      className="d-flex align-items-center gap-2"
                    >
                      <Flag
                        code={getFlagCode(
                          report.translations[0]?.language?.title ||
                            targetLanguageTitle
                        )}
                        style={{ width: 24, height: 24, marginLeft: 2 }}
                      />
                      <CapitalizeFirst text={translation.text} />
                    </div>
                  ))}
                </div>
              </Col>
            </Row>
          </div>
        ))}
      </Container>
    </>
  );
};

export default VocabularyListReports;
